import type {
  TopologyV2Edgeschema,
  TopologyV2Graphschema,
  TopologyV2Nodeschema,
  TopologyV2ResolveResponse,
  TopologyViewPatchOpschema
} from '@app/types/sdk';

export type TopologyStreamRef = NonNullable<TopologyV2Nodeschema['refs']>[number];
export type TopologyStreamNode = TopologyV2Nodeschema;
export type TopologyStreamEdge = TopologyV2Edgeschema;
export type TopologyStreamGraph = TopologyV2Graphschema;
export type TopologyStreamResolveResponse = TopologyV2ResolveResponse;

export type TopologyStreamOp = TopologyViewPatchOpschema;

export const resolveTopologyFrameType = (eventType: string | undefined, frameType: string | undefined): string => {
  const event = typeof eventType === 'string' ? eventType.trim() : '';
  if (event && event !== 'message') {
    return event;
  }
  return typeof frameType === 'string' ? frameType.trim() : event;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const asTopologyGraph = (value: unknown): TopologyStreamGraph | null => {
  const graph = asRecord(value);
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) {
    return null;
  }
  return graph as unknown as TopologyStreamGraph;
};

const asRecordArray = (value: unknown): Array<Record<string, unknown>> | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter((item): item is Record<string, unknown> => Boolean(asRecord(item)));
};

const emptyLegend = (): TopologyStreamResolveResponse['legend'] => ({ nodeRules: [], edgeRules: [] });

export const extractTopologySnapshot = (
  payload: Record<string, unknown> | undefined,
  previous?: TopologyStreamResolveResponse | null
): TopologyStreamResolveResponse | null => {
  const graph = asTopologyGraph(payload?.graph);
  if (!graph) {
    return null;
  }
  return {
    graph,
    legend: (asRecord(payload?.legend) as TopologyStreamResolveResponse['legend'] | undefined) ?? previous?.legend ?? emptyLegend(),
    drilldowns:
      (asRecordArray(payload?.drilldowns) as TopologyStreamResolveResponse['drilldowns'] | undefined) ?? previous?.drilldowns ?? [],
    overlayState:
      (asRecord(payload?.overlayState) as TopologyStreamResolveResponse['overlayState'] | undefined) ??
      previous?.overlayState,
    overlayStatuses:
      (Array.isArray(payload?.overlayStatuses)
        ? (payload?.overlayStatuses as TopologyStreamResolveResponse['overlayStatuses'])
        : previous?.overlayStatuses) ?? [],
    meta: asRecord(payload?.meta) ?? previous?.meta ?? {}
  };
};

const mergeStringMap = (base: Record<string, string> | undefined, incoming: Record<string, unknown>): Record<string, string> => {
  const next: Record<string, string> = { ...(base ?? {}) };
  Object.entries(incoming).forEach(([key, value]) => {
    if (typeof value === 'string') {
      next[key] = value;
    }
  });
  return next;
};

const mergeRuntimeUI = (
  base: Record<string, unknown> | undefined,
  incoming: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!base && !incoming) {
    return undefined;
  }
  const next: Record<string, unknown> = { ...(base ?? {}), ...(incoming ?? {}) };
  const baseRuntime = asRecord(base?.runtimeOverlays);
  const incomingRuntime = asRecord(incoming?.runtimeOverlays);
  if (baseRuntime || incomingRuntime) {
    next.runtimeOverlays = { ...(baseRuntime ?? {}), ...(incomingRuntime ?? {}) };
  }
  return next;
};

const mergeAnyMap = (base: Record<string, unknown> | undefined, incoming: Record<string, unknown>): Record<string, unknown> => {
  const next: Record<string, unknown> = {
    ...(base ?? {}),
    ...incoming
  };
  const baseUI = asRecord(base?.ui);
  const incomingUI = asRecord(incoming.ui);
  if (baseUI || incomingUI) {
    next.ui = mergeRuntimeUI(baseUI, incomingUI);
  }
  return next;
};

const normalizeTags = (current: string[] | undefined, payload: Record<string, unknown>): string[] => {
  const raw = payload?.tags;
  if (!Array.isArray(raw)) {
    return current ?? [];
  }
  return raw.map((item) => (typeof item === 'string' ? item.trim() : '')).filter((item) => item);
};

const addTags = (current: string[] | undefined, payload: Record<string, unknown>): string[] => {
  const next = new Set(current ?? []);
  normalizeTags(current, payload).forEach((tag) => next.add(tag));
  return Array.from(next);
};

const removeTags = (current: string[] | undefined, payload: Record<string, unknown>): string[] => {
  const remove = new Set(normalizeTags(current, payload));
  return (current ?? []).filter((tag) => !remove.has(tag));
};

const normalizeOpName = (op: string): string => (op === 'merge_props' ? 'merge_properties' : op);

const applyNodeOp = (node: TopologyStreamNode, op: string, payload: Record<string, unknown>): TopologyStreamNode => {
  switch (normalizeOpName(op)) {
    case 'merge_labels':
      return { ...node, labels: mergeStringMap(node.labels, payload) };
    case 'merge_properties':
      return { ...node, properties: mergeAnyMap(node.properties, payload) };
    case 'add_tags':
      return { ...node, tags: addTags(node.tags, payload) };
    case 'remove_tags':
      return { ...node, tags: removeTags(node.tags, payload) };
    case 'hide':
      return { ...node, properties: mergeAnyMap(node.properties, { 'ui.hidden': true }) };
    case 'show':
      return { ...node, properties: mergeAnyMap(node.properties, { 'ui.hidden': false }) };
    default:
      return node;
  }
};

const applyEdgeOp = (edge: TopologyStreamEdge, op: string, payload: Record<string, unknown>): TopologyStreamEdge => {
  switch (normalizeOpName(op)) {
    case 'merge_labels':
      return { ...edge, labels: mergeStringMap(edge.labels, payload) };
    case 'merge_properties':
      return { ...edge, properties: mergeAnyMap(edge.properties, payload) };
    case 'add_tags':
      return { ...edge, tags: addTags(edge.tags, payload) };
    case 'remove_tags':
      return { ...edge, tags: removeTags(edge.tags, payload) };
    case 'hide':
      return { ...edge, properties: mergeAnyMap(edge.properties, { 'ui.hidden': true }) };
    case 'show':
      return { ...edge, properties: mergeAnyMap(edge.properties, { 'ui.hidden': false }) };
    default:
      return edge;
  }
};

export const applyTopologyOpsToGraph = (graph: TopologyStreamGraph, ops: TopologyStreamOp[]): TopologyStreamGraph => {
  if (!ops || ops.length === 0) {
    return graph;
  }
  let changed = false;
  const nodes: TopologyStreamNode[] = graph.nodes.map((node) => ({
    ...node,
    labels: node.labels ? { ...node.labels } : undefined,
    tags: node.tags ? [...node.tags] : undefined,
    properties: node.properties ? { ...node.properties } : undefined
  }));
  const edges: TopologyStreamEdge[] = graph.edges.map((edge) => ({
    ...edge,
    labels: edge.labels ? { ...edge.labels } : undefined,
    tags: edge.tags ? [...edge.tags] : undefined,
    properties: edge.properties ? { ...edge.properties } : undefined
  }));

  const nodeIndex = new Map(nodes.map((node) => [node.id, node]));
  const edgeIndex = new Map(edges.map((edge) => [edge.id, edge]));

  ops.forEach((entry) => {
    const targetType = entry?.target?.type;
    const targetId = entry?.target?.id;
    const op = entry?.op ?? '';
    const payload = entry?.payload ?? {};
    if (!targetType || !targetId || !op) {
      return;
    }
    if (targetType === 'node') {
      const node = nodeIndex.get(targetId);
      if (node) {
        const nextNode = applyNodeOp(node, op, payload);
        if (nextNode !== node) {
          changed = true;
          nodeIndex.set(targetId, nextNode);
        }
      }
      return;
    }
    if (targetType === 'edge') {
      const edge = edgeIndex.get(targetId);
      if (edge) {
        const nextEdge = applyEdgeOp(edge, op, payload);
        if (nextEdge !== edge) {
          changed = true;
          edgeIndex.set(targetId, nextEdge);
        }
      }
    }
  });

  if (!changed) {
    return graph;
  }

  return {
    ...graph,
    nodes: nodes.map((node) => nodeIndex.get(node.id) ?? node),
    edges: edges.map((edge) => edgeIndex.get(edge.id) ?? edge)
  };
};
